import styled from '@emotion/styled';

import Box from '~/common/components/Box';
import { Table, Tbody, Td, Thead, Tr } from '~/common/components/Tables';
import Text from '~/common/components/Texts/Text';
import TitleCaseUi from '~/common/components/TitleCaseUi';
import { formatCurrency } from '~/common/util/format';
import type { WvoeCalculationResult } from '~/pages/IncomeCalculator/types';

import ConsideredFormulas from '../styles/ConsideredFormulas';

const TitleTd = TitleCaseUi(Td) as React.ComponentType<
  React.ComponentProps<typeof Td>
>;
const TitleTh = TitleCaseUi(styled.th`
  text-align: left;
  padding: 0.5rem 1rem;
  font-weight: 600;
`) as React.ComponentType<React.ThHTMLAttributes<HTMLTableCellElement>>;
const DlStyled = styled.dl`
  margin-top: 25px;
  display: flex;
  align-items: flex-end;
`;
const DtStyled = styled.dt`
  float: left;
  clear: left;
  font-weight: 500;
  margin-right: 4px;
  &:after {
    content: ':';
  }
`;

type FormulaDisplayProps = {
  calculationEquation: string;
  consideredFormulas?: {
    formula?: string;
    formulaType?: string;
    incomeValue?: number;
    isFormulaUsed?: boolean;
  }[];
};

function FormulaDisplay({
  calculationEquation,
  consideredFormulas,
}: Readonly<FormulaDisplayProps>) {
  if (consideredFormulas?.length) {
    return (
      <Box fontSize="xs" mt={4}>
        <Text fontWeight="bold" mb={1}>
          Calculations:
        </Text>
        <ConsideredFormulas consideredFormulas={consideredFormulas} />
      </Box>
    );
  }

  return (
    <Box as={DlStyled} fontSize="xs">
      <Text as={DtStyled} fontSize="xs">
        Calculation
      </Text>
      <Text as="dd" fontSize="xs">
        {calculationEquation}
      </Text>
    </Box>
  );
}

type WvoeSectionProps = Pick<
  WvoeCalculationResult['annualDetails'][number]['calculationResults'][number],
  'calculationEquation' | 'incomeDetails' | 'consideredFormulas'
>;

const WvoeSection = ({
  calculationEquation,
  incomeDetails,
  consideredFormulas,
}: Readonly<WvoeSectionProps>) => {
  const columnHeaders = ['{fiscalYear} (YTD)', '{fiscalYear}', '{fiscalYear}'];

  const rowHeaders: { key: 'monthlyIncome' | 'totalIncome'; label: string }[] =
    [
      { key: 'monthlyIncome', label: 'Monthly' },
      { key: 'totalIncome', label: 'Total' },
    ];
  return (
    <>
      <Table size="small" width="auto">
        <Thead>
          <Tr>
            <td></td>
            {columnHeaders.map((header, idx) => {
              const h = header.replace(
                '{fiscalYear}',
                idx in incomeDetails
                  ? String(incomeDetails[idx].fiscalYear)
                  : '####',
              );
              return (
                <TitleTh scope="col" key={h}>
                  {h}
                </TitleTh>
              );
            })}
          </Tr>
        </Thead>
        <Tbody>
          {rowHeaders.map(({ key, label }) => (
            <Tr key={key}>
              <TitleTd scope="row" sx={{ textAlign: 'right' }}>
                {label}
              </TitleTd>
              {incomeDetails.map((detail) => (
                <Td
                  key={`${key}-${detail.incomeYear}`}
                  sx={{ paddingRight: '24px' }}
                >
                  {formatCurrency(detail[key])}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <FormulaDisplay
        calculationEquation={calculationEquation}
        consideredFormulas={consideredFormulas}
      />
    </>
  );
};

WvoeSection.defaultProps = {
  isOverride: false,
  isOverrideError: false,
  overrideVal: '0.00',
};

export default WvoeSection;
